import { useEffect, useRef, useMemo } from "preact/hooks";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { SkeletonLoader } from "../SkeletonLoader";
import { ErrorMessage } from "../ErrorMessage";
import { formatBps, formatMs } from "../../utils/format";
import {
  getChartColors,
  isoToUnixSeconds,
  createTooltip,
  makeTimeFormatter,
  formatBpsAxis,
  formatMsAxis,
  useChartTheme,
} from "./chartUtils";

type SpeedtestPoint = {
  timestamp: string;
  download: number;
  upload: number;
  ping: number;
};

type SpeedtestChartProps = {
  data: {
    mikrotik: SpeedtestPoint[];
    ookla: SpeedtestPoint[];
  };
  type: "download" | "upload" | "ping";
  loading?: boolean;
  error?: string;
};

const typeLabels: Record<SpeedtestChartProps["type"], string> = {
  download: "Download",
  upload: "Upload",
  ping: "Ping",
};

export function SpeedtestChart({
  data,
  type,
  loading,
  error,
}: SpeedtestChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const theme = useChartTheme();

  const { timestamps, mikrotikValues, ooklaValues, hasData } = useMemo(() => {
    const set = new Set<number>();
    data.mikrotik.forEach((d) => set.add(isoToUnixSeconds(d.timestamp)));
    data.ookla.forEach((d) => set.add(isoToUnixSeconds(d.timestamp)));
    const timestampsArr = Array.from(set).sort((a, b) => a - b);

    const mkMap = new Map(
      data.mikrotik.map((d) => [isoToUnixSeconds(d.timestamp), d[type]])
    );
    const okMap = new Map(
      data.ookla.map((d) => [isoToUnixSeconds(d.timestamp), d[type]])
    );

    const mkValues = timestampsArr.map((t) => mkMap.get(t) ?? null);
    const okValues = timestampsArr.map((t) => okMap.get(t) ?? null);

    return {
      timestamps: timestampsArr,
      mikrotikValues: mkValues,
      ooklaValues: okValues,
      hasData: timestampsArr.length > 0,
    };
  }, [data, type]);

  useEffect(() => {
    if (loading || error || !hasData) return;
    if (!containerRef.current) return;

    const colors = getChartColors();
    const tooltip = createTooltip();
    tooltipRef.current = tooltip;
    containerRef.current.appendChild(tooltip);

    const width = containerRef.current.clientWidth;
    const height = 300;
    const valueFormatter = type === "ping" ? formatMs : formatBps;
    const axisFormatter = type === "ping" ? formatMsAxis : formatBpsAxis;

    const opts: uPlot.Options = {
      width,
      height,
      cursor: { drag: { x: false, y: false } },
      legend: { show: true },
      axes: [
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: makeTimeFormatter(timestamps),
        },
        {
          stroke: colors.text,
          grid: { stroke: colors.grid },
          ticks: { stroke: colors.grid },
          values: axisFormatter,
          size: 70,
        },
      ],
      series: [
        {},
        {
          label: "MikroTik",
          stroke: colors.blue,
          width: 2,
          points: {
            show: true,
            size: 5,
            stroke: colors.blue,
            fill: colors.blue,
          },
        },
        {
          label: "Ookla",
          stroke: colors.green,
          width: 2,
          dash: [6, 4],
          points: {
            show: true,
            size: 5,
            stroke: colors.green,
            fill: colors.green,
          },
        },
      ],
      hooks: {
        setCursor: [
          (u) => {
            const idx = u.cursor.idx;
            if (idx == null || idx < 0) {
              tooltip.style.display = "none";
              return;
            }
            const t = u.data[0][idx];
            const mkVal = u.data[1][idx];
            const okVal = u.data[2][idx];
            if (t == null) {
              tooltip.style.display = "none";
              return;
            }
            tooltip.innerHTML = `
              <div><strong>${typeLabels[type]}</strong></div>
              <div><small>${new Date(t * 1000).toLocaleString("id-ID")}</small></div>
              <div style="color:${colors.blue}">MikroTik: ${mkVal != null ? valueFormatter(mkVal) : "-"}</div>
              <div style="color:${colors.green}">Ookla: ${okVal != null ? valueFormatter(okVal) : "-"}</div>
            `;
            tooltip.style.display = "block";
            tooltip.style.left = `${(u.cursor.left ?? 0) + 12}px`;
            tooltip.style.top = `${(u.cursor.top ?? 0) + 12}px`;
          },
        ],
      },
    };

    const chart = new uPlot(
      opts,
      [timestamps, mikrotikValues, ooklaValues],
      containerRef.current
    );
    chartRef.current = chart;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        chart.setSize({ width: entry.contentRect.width, height });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.destroy();
      tooltip.remove();
      chartRef.current = null;
      tooltipRef.current = null;
    };
  }, [timestamps, mikrotikValues, ooklaValues, type, theme, loading, error, hasData]);

  if (loading) return <SkeletonLoader type="chart" />;
  if (error) return <ErrorMessage message={error} />;
  if (!hasData) return <div class="chart-empty">Belum ada data</div>;

  return <div ref={containerRef} class="chart-container" />;
}
